import React from 'react';
import { motion } from 'framer-motion';

interface WrappedCardProps {
  children: React.ReactNode;
  direction: number;
  onNext?: () => void;
  onPrev?: () => void;
  className?: string;
}

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.85,
    rotateY: direction > 0 ? 15 : -15,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    rotateY: 0,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.85,
    rotateY: direction < 0 ? 15 : -15,
  }),
};

const WrappedCard: React.FC<WrappedCardProps> = ({
  children,
  direction,
  onNext,
  onPrev,
  className = '',
}) => {
  return (
    <motion.div
      custom={direction}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{
        x: { type: "spring", stiffness: 150, damping: 22 },
        opacity: { duration: 0.5, ease: "easeInOut" },
        scale: { duration: 0.5, ease: "easeOut" },
        rotateY: { duration: 0.5, ease: "easeInOut" },
      }}
      className={`absolute inset-0 w-full h-full flex flex-col items-center justify-center overflow-hidden bg-transparent text-white ${className}`}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={(e, { offset, velocity }) => {
        const swipe = offset.x;

        if (swipe < -50) {
          onNext && onNext();
        } else if (swipe > 50) {
          onPrev && onPrev();
        }
      }}
    >
      {/* Navigation Areas */}
      <div className="absolute inset-y-0 left-0 w-1/6 z-20" onClick={onPrev} />
      <div className="absolute inset-y-0 right-0 w-1/6 z-20" onClick={onNext} />

      {/* Content */}
      <div className="relative z-10 w-full h-full flex flex-col">
        {children}
      </div>
    </motion.div>
  );
};

export default WrappedCard;
